import {
  POPOVER_ANIMATION_CLOSE_COMMAND,
  POPOVER_ANIMATION_OPEN_COMMAND,
  POPOVER_COMMAND_EVENT,
} from '@packages/systems/core-constants';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';

// Re-exported so existing importers keep their path while `core-constants`
// stays the single declaration.
export {POPOVER_COMMAND_EVENT};

export type PopoverCommand =
  | {
      type: 'show';
      nativeIdPath?: readonly NativeId[];
      source?: Element;
    }
  | {type: 'selection-show'}
  | {type: 'hide'}
  | {type: 'set-forced-closed'; forcedClosed: boolean}
  // Sent by IX3 to the published site module, which owns visibility on
  // published pages and in preview. The canvas ignores them.
  // `source` is the element the causing trigger fired on, which the site module
  // uses as the popover's anchor when it is one of that popover's own invokers.
  | {type: typeof POPOVER_ANIMATION_OPEN_COMMAND; source?: Element}
  | {type: typeof POPOVER_ANIMATION_CLOSE_COMMAND};

export const dispatchPopoverCommand = (
  target: HTMLElement,
  command: PopoverCommand
) => {
  const CustomEventConstructor = target.ownerDocument.defaultView?.CustomEvent;
  if (!CustomEventConstructor) return;

  target.dispatchEvent(
    new CustomEventConstructor(POPOVER_COMMAND_EVENT, {detail: command})
  );
};

export const listenForPopoverCommands = (
  target: HTMLElement,
  listener: (command: PopoverCommand) => void
) => {
  const handleCommand = (event: Event) => {
    const command = (event as CustomEvent<PopoverCommand>).detail;
    if (command) listener(command);
  };

  target.addEventListener(POPOVER_COMMAND_EVENT, handleCommand);
  return () => target.removeEventListener(POPOVER_COMMAND_EVENT, handleCommand);
};

export const isPopoverOpen = (target: HTMLElement): boolean => {
  try {
    return target.matches(':popover-open');
  } catch {
    return false;
  }
};
